import { useCallback, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import Axios from "../../../Axios";
import { playSound } from "../../Soundmaker";
import { orderStickyEndTimes } from "../constants/upDownConstants";
import { rememberResultOwner } from "../utils/resultOwnerManager";
import {
  clamp,
  roundToCents,
  clampStake,
  parseStake,
  toAmountInput,
  toTradeDateTime,
  toBackendPairSymbol,
  normalizeBackendPairType,
} from "../utils/upDownFormatters";
import {
  getOrderTimeDetails,
  getProfitPercentage,
} from "../utils/earlyCloseUtils";

export const parseTimerInputSeconds = (value) => {
  const rawValue = String(value || "").trim();
  if (rawValue.includes(":")) {
    const [minutes = "0", seconds = "0"] = rawValue.split(":");
    const parsedMinutes = Number(minutes.replace(/\D/g, "")) || 0;
    const parsedSeconds = Number(seconds.replace(/\D/g, "")) || 0;
    return parsedMinutes * 60 + parsedSeconds;
  }

  const digits = rawValue.replace(/\D/g, "");
  return digits === "" ? 0 : Number(digits);
};

/**
 * Custom hook to manage order placement, order settling status,
 * early trade close preview, and early cashout confirmation.
 */
export function useUpDownOrders({
  wallet,
  amount,
  amountInput,
  setAmountInput,
  symbolActiveOrdersCount,
  activePairLabel,
  chartRef,
  layoutRef,
  setActiveResultLayoutRef,
  tradingAPIRef,
  variant,
  upPayoutPercent,
  downPayoutPercent,
  selectedExpirySeconds,
  timerInput,
  panelId,
  luckyData,
  activeOrders,
  setActiveOrders,
  setLatestOrder,
  selectedPair,
  pairType,
  getWallet,
  livePrice,
  clockNow,
  tickerOffset,
  dynamicPayoutPercent,
  userRank,
  localResultOrderIdsRef,
  chartOrderHydrationRef,
  backendIdToLocalIdRef,
  ignoredBackendTradeIdsRef,
  orderRemovalTimersRef,
  settledOrderIdsRef,
}) {
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [isCancelModalClosing, setIsCancelModalClosing] = useState(false);
  const lastCancelOrderRef = useRef(null);
  const lastClosePreviewRef = useRef(null);

  const handlePlaceOrder = useCallback(
    async (side) => {
      if (!wallet || (wallet.balance < amount && wallet.credit < amount)) {
        toast.error("Insufficient balance");
        return;
      } else if (amount > wallet?.binary?.max_amount) {
        toast.error(`Maximum amount is ${wallet?.binary?.max_amount}`);
        return;
      } else if (amount < wallet?.binary?.min_amount) {
        toast.error(`Minimum amount is ${wallet?.binary?.min_amount}`);
        return;
      }

      if (symbolActiveOrdersCount >= 3) {
        toast.error(
          `You can have maximum of 3 active trades for ${activePairLabel}`,
          { id: "trade-limit-error" },
        );
        return;
      }

      if (chartRef?.current?.isChartLoading?.()) {
        toast.error("Chart is still loading, please wait...");
        return;
      }

      // Play button sound immediately for instant UI feedback
      playSound("button");

      if (setActiveResultLayoutRef) {
        setActiveResultLayoutRef(layoutRef ?? null);
      }

      let finalAmount = amountInput;
      // Allow overriding amount via tradingAPIRef
      if (tradingAPIRef?.current?.overrideAmount !== undefined) {
        finalAmount = tradingAPIRef.current.overrideAmount;
        tradingAPIRef.current.overrideAmount = undefined; // consume it
      }

      const normalizedAmount = clampStake(
        parseStake(finalAmount),
        variant.minimumStake,
        variant.maximumStake,
      );
      setAmountInput(toAmountInput(normalizedAmount));
      const payoutPercent = side === "up" ? upPayoutPercent : downPayoutPercent;

      let secondsToSettlement = selectedExpirySeconds;
      if (tradingAPIRef?.current?.overrideDuration !== undefined) {
        secondsToSettlement = tradingAPIRef.current.overrideDuration;
        tradingAPIRef.current.overrideDuration = undefined; // consume it
      }

      const entryPrice = Number(chartRef?.current?.getCurrentPrice?.());
      const chartTickTime = Number(chartRef?.current?.getCurrentTickTime?.());
      if (
        !Number.isFinite(entryPrice) ||
        entryPrice <= 0 ||
        !Number.isFinite(chartTickTime) ||
        chartTickTime <= 0
      ) {
        toast.error("Live price is not ready. Please try again.");
        return;
      }

      const nowMs = Date.now();
      const entryTime = nowMs / 1000;
      const expiryTime = entryTime + secondsToSettlement;
      const orderId = `order-${entryTime}-${Math.random().toString(16).slice(2, 8)}`;
      if (localResultOrderIdsRef?.current) {
        localResultOrderIdsRef.current.add(orderId);
      }
      rememberResultOwner(panelId, orderId);

      const activeLuckyMultiplier = Number(
        luckyData?.isLucky && Number(luckyData?.multiplier) > 1
          ? luckyData.multiplier
          : Number(wallet?.binary?.activeMultiplier) > 1
            ? wallet.binary.activeMultiplier
            : 1,
      );
      const isLuckyTrade = activeLuckyMultiplier > 1;

      // Optimistically clear active multiplier across UI immediately (0ms delay)
      if (isLuckyTrade && typeof window !== "undefined") {
        try {
          sessionStorage.setItem("ez5050_last_order_lucky", "true");
        } catch (_e) {}
        window.dispatchEvent(new CustomEvent("clear-lucky-multiplier"));
      }

      const optimisticOrder = chartRef?.current?.placeOrder?.({
        orderId,
        side,
        amount: normalizedAmount,
        durationSeconds: secondsToSettlement,
        entryPrice,
        entryTime,
        expiryTime,
        payoutMultiplier: 1 + payoutPercent / 100,
        tradeMultiplier: activeLuckyMultiplier,
        jackpot_multiplier: activeLuckyMultiplier,
        isLuckyRound: isLuckyTrade,
      });

      if (optimisticOrder) {
        if (chartOrderHydrationRef?.current) {
          chartOrderHydrationRef.current.add(orderId);
        }
        setLatestOrder(optimisticOrder);
        setActiveOrders((previous) => [
          {
            ...optimisticOrder,
            side: side,
            pair: activePairLabel,
            createdAtMs: nowMs,
            expiresAtMs: nowMs + secondsToSettlement * 1000,
            startTime: toTradeDateTime(entryTime),
            endTime: toTradeDateTime(expiryTime),
            status: "active",
            tradeMultiplier: activeLuckyMultiplier,
            jackpot_multiplier: activeLuckyMultiplier,
            isLuckyRound: isLuckyTrade,
          },
          ...previous,
        ]);

        try {
          window.dispatchEvent(
            new CustomEvent("globalOrderMarker", {
              detail: {
                orderId,
                side,
                entryPrice,
                amount: normalizedAmount,
                payoutMultiplier: 1 + payoutPercent / 100,
                tradeMultiplier: activeLuckyMultiplier,
                jackpot_multiplier: activeLuckyMultiplier,
                isLuckyRound: isLuckyTrade,
                entryTime,
                expiryTime,
                marketSymbol: toBackendPairSymbol(
                  selectedPair,
                  activePairLabel,
                ),
              },
            }),
          );
        } catch (e) {
          // ignore in non-browser environments
        }
      }

      const payload = {
        order_id: orderId,
        direction: side,
        start_price: String(entryPrice),
        duration: String(parseTimerInputSeconds(timerInput)),
        startTime: toTradeDateTime(entryTime),
        endTime: toTradeDateTime(expiryTime),
        pair: toBackendPairSymbol(selectedPair, activePairLabel),
        pair_type: normalizeBackendPairType(pairType),
        amount: String(amount),
        type: "binary",
      };

      try {
        const { data } = await Axios.post("/users/placeTrade", payload);
        if (data?.success) {
          const backendId = String(data?.result?._id || data?.result?.id || "");
          if (backendId) {
            if (backendIdToLocalIdRef?.current) {
              backendIdToLocalIdRef.current.set(backendId, orderId);
            }
            if (ignoredBackendTradeIdsRef?.current) {
              ignoredBackendTradeIdsRef.current.add(backendId);
            }
          }

          const backendMultiplier = Number(data?.result?.jackpot_multiplier || 1);
          const backendIsLucky = Boolean(data?.result?.isLuckyRound);
          if (backendMultiplier > 1 && backendIsLucky) {
            setActiveOrders((prev) =>
              prev.map((o) =>
                o.id === orderId
                  ? {
                      ...o,
                      tradeMultiplier: backendMultiplier,
                      jackpot_multiplier: backendMultiplier,
                      isLuckyRound: true,
                    }
                  : o,
              ),
            );
          }

          if (typeof getWallet === "function") {
            getWallet();
          }

          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("refresh-lucky-details"));
          }

          const forceLoss = data?.result?.forceLoss ?? data?.forceLoss ?? false;
          if (forceLoss && optimisticOrder) {
            optimisticOrder.forceLoss = forceLoss;
          }
        } else {
          chartRef?.current?.removeOrder?.(orderId);
          if (chartOrderHydrationRef?.current) {
            chartOrderHydrationRef.current.delete(orderId);
          }
          setActiveOrders((previous) =>
            previous.filter((order) => order.id !== orderId),
          );
          setLatestOrder((previous) =>
            previous?.id === orderId ? null : previous,
          );
          toast.error(data?.message || "Failed to place trade");
          if (typeof getWallet === "function") {
            getWallet();
          }
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("refresh-lucky-details"));
          }
        }
      } catch (error) {
        chartRef?.current?.removeOrder?.(orderId);
        if (chartOrderHydrationRef?.current) {
          chartOrderHydrationRef.current.delete(orderId);
        }
        setActiveOrders((previous) =>
          previous.filter((order) => order.id !== orderId),
        );
        setLatestOrder((previous) =>
          previous?.id === orderId ? null : previous,
        );
        console.error("Error placing trade:", error);
        toast.error(error?.response?.data?.message || "Failed to place trade", {
          id: "trade-error",
        });
        if (typeof getWallet === "function") {
          getWallet();
        }
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("refresh-lucky-details"));
        }
      }
    },
    [
      wallet,
      amount,
      amountInput,
      symbolActiveOrdersCount,
      activePairLabel,
      chartRef,
      layoutRef,
      setActiveResultLayoutRef,
      tradingAPIRef,
      variant.minimumStake,
      variant.maximumStake,
      upPayoutPercent,
      downPayoutPercent,
      selectedExpirySeconds,
      timerInput,
      panelId,
      luckyData,
      setActiveOrders,
      setLatestOrder,
      selectedPair,
      pairType,
      getWallet,
      localResultOrderIdsRef,
      chartOrderHydrationRef,
      backendIdToLocalIdRef,
      ignoredBackendTradeIdsRef,
      setAmountInput,
    ],
  );

  const handleOrderSettled = useCallback((settledOrder) => {
    const orderId = String(settledOrder?.id || "").trim();
    if (!orderId) {
      return;
    }

    // Trade duration completed on chart. Mark status as "settling" while awaiting
    // the authoritative backend websocket event (binaryTradeResult).
    setActiveOrders((previous) =>
      previous?.map((order) =>
        order.id === orderId
          ? {
              ...order,
              status: order.status === "active" ? "settling" : order.status,
            }
          : order,
      ),
    );

    setLatestOrder((previous) => {
      if (!previous || previous.id !== orderId) {
        return previous;
      }
      return {
        ...previous,
        status: previous.status === "active" ? "settling" : previous.status,
      };
    });
  }, [setActiveOrders, setLatestOrder]);

  const cancelOrder = useMemo(
    () => activeOrders?.find((order) => order.id === cancelOrderId) ?? null,
    [activeOrders, cancelOrderId],
  );

  const closeTradePreview = useMemo(() => {
    if (!cancelOrder) {
      return null;
    }

    const currentPriceValue = Number(chartRef?.current?.getCurrentPrice?.());
    const currentPrice =
      Number.isFinite(currentPriceValue) && currentPriceValue > 0
        ? currentPriceValue
        : Number(livePrice);
    const entryPrice = Number(cancelOrder.entryPrice);
    const amountValue = Number(cancelOrder.amount);

    const nowMs = clockNow || Date.now();
    const stickyEnd = orderStickyEndTimes.get(cancelOrder.id);
    const timeDetails = getOrderTimeDetails(cancelOrder, stickyEnd, nowMs);

    const {
      durationSeconds,
      remainingSeconds,
      progressPercentage,
      endMs,
    } = timeDetails;

    const isWinning =
      cancelOrder.side === "up"
        ? currentPrice > entryPrice
        : currentPrice < entryPrice;
    const isDraw =
      Number.isFinite(currentPrice) &&
      Number.isFinite(entryPrice) &&
      currentPrice === entryPrice;

    const profitPercent = Number(
      getProfitPercentage(
        progressPercentage,
        wallet?.binary?.profit_details,
        wallet?.binary?.win_percentage || dynamicPayoutPercent,
        userRank,
        isWinning,
      ),
    );

    const ratio = clamp(remainingSeconds / durationSeconds, 0, 1);

    let closePayout = amountValue;
    let closeProfit = 0;
    if (isDraw) {
      closePayout = amountValue;
      closeProfit = 0;
    } else if (isWinning) {
      closeProfit = roundToCents((amountValue * profitPercent) / 100);
      closePayout = roundToCents(amountValue + closeProfit);
    } else {
      const absLoss = Math.abs(profitPercent);
      const lossAmount = roundToCents((amountValue * absLoss) / 100);
      closeProfit = -lossAmount;
      closePayout = roundToCents(Math.max(0, amountValue - lossAmount));
    }

    const displayPercent = isDraw
      ? 0
      : isWinning
        ? Math.abs(profitPercent)
        : -Math.abs(profitPercent);

    return {
      currentPrice,
      entryPrice,
      amount: amountValue,
      durationSeconds,
      expiryTime: endMs ? endMs / 1000 : 0,
      remainingSeconds,
      ratio,
      isWinning,
      closeProfit,
      closePercent: isDraw ? 100 : Math.max(0, 100 - profitPercent),
      closePayout,
      displayPercent,
    };
  }, [
    cancelOrder,
    chartRef,
    livePrice,
    clockNow,
    wallet?.binary?.profit_details,
    wallet?.binary?.win_percentage,
    dynamicPayoutPercent,
    userRank,
  ]);

  if (cancelOrder) {
    lastCancelOrderRef.current = cancelOrder;
  }
  if (closeTradePreview) {
    lastClosePreviewRef.current = closeTradePreview;
  }

  const closeCancelModal = useCallback(() => {
    if (isCancelModalClosing) return;
    setIsCancelModalClosing(true);
    setTimeout(() => {
      setIsCancelModalClosing(false);
      setCancelOrderId(null);
    }, 220);
  }, [isCancelModalClosing]);

  const handleConfirmCancelOrder = useCallback(async () => {
    try {
      if (!cancelOrderId || !cancelOrder) {
        return;
      }

      const { data } = await Axios.post("/users/closeBinaryTrade", {
        trade_id: cancelOrderId,
      });

      if (data?.success) {
        const removalTimer = orderRemovalTimersRef?.current?.get(cancelOrderId);
        if (removalTimer) {
          clearTimeout(removalTimer);
          orderRemovalTimersRef.current.delete(cancelOrderId);
        }
        chartRef?.current?.removeOrder?.(cancelOrderId);
        if (chartOrderHydrationRef?.current) {
          chartOrderHydrationRef.current.delete(cancelOrderId);
        }
        // Prevent getWallet / socket from re-adding this closed order
        if (settledOrderIdsRef?.current) {
          settledOrderIdsRef.current.add(cancelOrderId);
          setTimeout(() => settledOrderIdsRef.current.delete(cancelOrderId), 30000);
        }
        setActiveOrders((previous) =>
          previous.filter((order) => order.id !== cancelOrderId),
        );
        setLatestOrder((previous) => {
          if (!previous || previous.id !== cancelOrderId) {
            return previous;
          }
          const remainingOrders = activeOrders?.filter(
            (order) => order.id !== cancelOrderId,
          );
          return remainingOrders[remainingOrders.length - 1] ?? null;
        });

        setTimeout(() => {
          setIsCancelModalClosing(false);
          setCancelOrderId(null);
        }, 180);
        toast.success("Trade closed early");
      } else {
        toast.error(data?.message || "Failed to close trade");
      }
    } catch (error) {
      console.log(error);
    }
  }, [
    cancelOrder,
    cancelOrderId,
    chartRef,
    chartOrderHydrationRef,
    orderRemovalTimersRef,
    settledOrderIdsRef,
    setActiveOrders,
    setLatestOrder,
    activeOrders,
  ]);

  return {
    handlePlaceOrder,
    handleOrderSettled,
    cancelOrderId,
    setCancelOrderId,
    cancelOrder,
    closeTradePreview,
    isCancelModalClosing,
    lastCancelOrderRef,
    lastClosePreviewRef,
    closeCancelModal,
    handleConfirmCancelOrder,
  };
}

